import { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';
import Button from './Button';
import { cx } from './util';

const FOCUSABLE = 'button:not(:disabled), [href], input:not(:disabled), select, textarea, [tabindex]:not([tabindex="-1"])';

// Focus enters the dialog, Tab stays inside, Escape closes, focus returns to where it was. Dialogs stack (a schedule
// dialog over a day's matches, a confirm over a form): Escape and the Tab trap act on the TOPMOST open dialog only.
const openStack = [];
export function useDialogFocus(open, ref, onClose) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const token = {};
    openStack.push(token);
    const back = document.activeElement;
    const first = ref.current?.querySelector('[data-autofocus]') || ref.current?.querySelector(FOCUSABLE);
    first?.focus();
    const onKey = e => {
      if (openStack[openStack.length - 1] !== token) return;
      if (e.key === 'Escape') { e.stopPropagation(); closeRef.current?.(); return; }
      if (e.key !== 'Tab' || !ref.current) return;
      const els = [...ref.current.querySelectorAll(FOCUSABLE)];
      if (!els.length) return;
      const [a, z] = [els[0], els[els.length - 1]];
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      openStack.splice(openStack.indexOf(token), 1);
      back?.focus?.();
    };
  }, [open, ref]);
}

// §2.6 Modal: create flows only (view and edit use the Expanded-row panel). footer: buttons. size: 'medium' (680) |
// 'wide' (920); default 520. meta: a line under the title. open defaults to true, so a Modal can also be mounted
// conditionally ({show && <Modal …>}).
export default function Modal({ open = true, onClose, title, meta, children, footer, size, testId }) {
  const box = useRef(null);
  const titleId = useId();
  useDialogFocus(open, box, onClose);
  if (!open) return null;
  return (
    <div className="ui ui-scrim" data-testid={testId} onMouseDown={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <section className={cx('ui-modal', size && `ui-modal--${size}`)} role="dialog" aria-modal="true" aria-labelledby={titleId} ref={box}>
        <header className="ui-modal__head">
          {meta ? (
            <div className="ui-ph__titles">
              <h2 className="ui-modal__title" id={titleId}>{title}</h2>
              <p className="ui-ph__meta">{meta}</p>
            </div>
          ) : <h2 className="ui-modal__title" id={titleId}>{title}</h2>}
          {meta && <span className="ui-spacer" />}
          <Button variant="quiet" iconOnly icon={X} label="Close" onClick={onClose} />
        </header>
        <div className="ui-modal__body">{children}</div>
        {footer && <footer className="ui-modal__foot">{footer}</footer>}
      </section>
    </div>
  );
}
